// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The mini-map on the bench, in Three (M13-23).
//
// `miniMap.ts` says where the map floats and where its arrows are; this draws it under
// `MachineScene.playfield`: a dark face lying flat over the surface at `MAP_LIFT`, drawn at
// `1 / scale`; on it the **plan**, a canvas painted by the 2-D Builder's own `paint()` — the same
// grid, rails and device shapes the canvas and Test play's `BallMap` draw, the selected part in
// the selection colour — repainted only when the map's key changes (the machine, the selection,
// the side); and under it the **pads**, eight arrows on a second canvas, lit while a part is
// selected and dim while none is, repainted when that changes. The layout is `NUDGE_ARROWS`',
// the same the input measures its taps against, so what is drawn is what is touched.
//
// In a build without a `document` — a test, a headless runtime — there are no canvases and the
// map is its face and its two planes, placed as they would be.

import { CanvasTexture, Group, LinearFilter, Mesh, MeshBasicMaterial, PlaneGeometry, SRGBColorSpace } from 'three'
import { DARK_CANVAS, paint } from '../builder/paint'
import { MAP_LIFT, MAP_WIDTH, NUDGE_ARROWS, PAD_CELL, PLAN_CANVAS, planViewport, type MiniMapView } from '../xr/build/miniMap'
import { TARGET } from '../xr/build/inspector'

/** The plan's canvas: one to two, as the plan's area is. */
/** The pads' canvas, and the strip of the map it covers: the whole width, the pads' three cells — square pixels. */
const PAD_STRIP_HEIGHT = PAD_CELL * 3
const PAD_CANVAS = { width: 512, height: Math.round((512 * PAD_STRIP_HEIGHT) / MAP_WIDTH) }
const FACE_RGBA = 'rgba(12, 10, 20, 0.86)'
const INK = '#e8e6f0'
const DIM = '#8a879a'
const ARROW_CSS = '#9fd8ff'
const OVER_FACE = 0.0008

type DocumentLike = { createElement: (tag: string) => HTMLCanvasElement }

function canvasOf(width: number, height: number): HTMLCanvasElement | undefined {
  const host = (globalThis as { document?: DocumentLike }).document
  const canvas = host?.createElement('canvas')
  if (canvas === undefined) return undefined
  canvas.width = width
  canvas.height = height
  return canvas
}

interface Painted {
  readonly mesh: Mesh
  readonly geometry: PlaneGeometry
  readonly material: MeshBasicMaterial
  readonly canvas: HTMLCanvasElement | undefined
  readonly texture: CanvasTexture | undefined
}

function paintedPlane(width: number, height: number, canvasWidth: number, canvasHeight: number, name: string, fallback: number, fallbackOpacity: number): Painted {
  const canvas = canvasOf(canvasWidth, canvasHeight)
  let texture: CanvasTexture | undefined
  if (canvas !== undefined) {
    texture = new CanvasTexture(canvas)
    texture.colorSpace = SRGBColorSpace
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
  }
  const geometry = new PlaneGeometry(width, height)
  const material = new MeshBasicMaterial({
    ...(texture === undefined ? { color: fallback } : { map: texture }),
    transparent: true,
    opacity: texture === undefined ? fallbackOpacity : 1,
    depthTest: false,
    depthWrite: false,
  })
  const mesh = new Mesh(geometry, material)
  mesh.name = name
  mesh.renderOrder = 1003
  mesh.frustumCulled = false
  return { mesh, geometry, material, canvas, texture }
}

function disposePainted(p: Painted): void {
  p.geometry.dispose()
  p.material.dispose()
  p.texture?.dispose()
}

/** The plan: the Builder's own painting of the machine, fitted to the canvas. */
function paintPlan(plane: Painted, view: MiniMapView): void {
  const { canvas, texture } = plane
  if (canvas === undefined || texture === undefined) return
  const ctx = canvas.getContext('2d')
  if (ctx === null) return
  paint(ctx, {
    // The plan's own viewport — zoomed to the selection when there is one — the one a tap is read with.
    view: planViewport(view),
    playfield: view.playfield,
    devices: view.source.devices,
    layers: view.source.layers,
    selected: view.source.selected,
    theme: DARK_CANVAS,
    // No reachability wash: the overlay on the bench already says where a ball can go (M13-17).
    showValidation: false,
  })
  texture.needsUpdate = true
}

/** The pads: eight arrows as triangles in their cells, and the millimetres under each pad. */
function paintPads(plane: Painted, view: MiniMapView): void {
  const { canvas, texture } = plane
  if (canvas === undefined || texture === undefined) return
  const ctx = canvas.getContext('2d')
  if (ctx === null) return
  const ppm = canvas.width / view.width
  // The strip is the bottom `PAD_STRIP_HEIGHT` of the map; its top edge is where the canvas's y = 0 is.
  const px = (x: number): number => (x + view.width / 2) * ppm
  const py = (y: number): number => (-view.height / 2 + PAD_STRIP_HEIGHT - y) * ppm
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  const ink = view.enabled ? INK : DIM
  const edge = view.enabled ? ARROW_CSS : DIM
  for (const arrow of NUDGE_ARROWS) {
    const cx = px(arrow.x)
    const cy = py(arrow.y)
    const half = (TARGET / 2) * ppm
    ctx.fillStyle = 'rgba(255, 255, 255, 0.07)'
    ctx.beginPath()
    ctx.rect(cx - half, cy - half, half * 2, half * 2)
    ctx.fill()
    ctx.strokeStyle = edge
    ctx.lineWidth = 0.0006 * ppm
    ctx.stroke()
    // The triangle, pointing the way the part goes: +y is up the playfield, which is up on the map.
    const r = half * 0.55
    const ux = Math.sign(arrow.dx)
    const uy = -Math.sign(arrow.dy)
    ctx.fillStyle = ink
    ctx.beginPath()
    ctx.moveTo(cx + ux * r, cy + uy * r)
    ctx.lineTo(cx - uy * r * 0.8 - ux * r * 0.6, cy + ux * r * 0.8 - uy * r * 0.6)
    ctx.lineTo(cx + uy * r * 0.8 - ux * r * 0.6, cy - ux * r * 0.8 - uy * r * 0.6)
    ctx.fill()
  }
  // The millimetres, centred under each pad.
  ctx.fillStyle = ink
  ctx.font = `600 ${Math.round(0.007 * ppm)}px system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  for (const [mm, arrows] of [
    [1, NUDGE_ARROWS.filter((a) => a.mm === 1)],
    [10, NUDGE_ARROWS.filter((a) => a.mm === 10)],
  ] as const) {
    const x = arrows.reduce((sum, a) => sum + a.x, 0) / arrows.length
    const y = arrows.reduce((sum, a) => sum + a.y, 0) / arrows.length
    ctx.fillText(`${mm} mm`, px(x), py(y))
  }
  texture.needsUpdate = true
}

export interface MiniMapBuild {
  readonly group: Group
  update(view: MiniMapView | undefined, scale: number): void
  /** What the plan was last painted for, for a test with no canvas to read. */
  readonly planKey: string | undefined
  /** Whether the pads were last painted lit. */
  readonly padsLit: boolean | undefined
  dispose(): void
}

export function buildMiniMap(): MiniMapBuild {
  const group = new Group()
  group.name = 'build.map'
  group.visible = false
  const pivot = new Group()
  pivot.name = 'map-card'
  group.add(pivot)

  let face: Painted | undefined
  let plan: Painted | undefined
  let pads: Painted | undefined
  let planKey: string | undefined
  let padsLit: boolean | undefined

  const dropAll = (): void => {
    for (const p of [face, plan, pads]) {
      if (p === undefined) continue
      pivot.remove(p.mesh)
      disposePainted(p)
    }
    face = undefined
    plan = undefined
    pads = undefined
    planKey = undefined
    padsLit = undefined
  }

  const lay = (view: MiniMapView): void => {
    if (face !== undefined) return
    face = paintedPlane(view.width, view.height, 8, 8, 'map-face', 0x0c0a14, 0.86)
    if (face.canvas !== undefined && face.texture !== undefined) {
      const ctx = face.canvas.getContext('2d')
      if (ctx !== null) {
        ctx.fillStyle = FACE_RGBA
        ctx.fillRect(0, 0, face.canvas.width, face.canvas.height)
        face.texture.needsUpdate = true
      }
    }
    pivot.add(face.mesh)
    plan = paintedPlane(view.plan.width, view.plan.height, PLAN_CANVAS.width, PLAN_CANVAS.height, 'map-plan', 0x0d1015, 1)
    plan.mesh.position.set(view.plan.x, view.plan.y, OVER_FACE)
    plan.mesh.renderOrder = 1004
    pivot.add(plan.mesh)
    pads = paintedPlane(view.width, PAD_STRIP_HEIGHT, PAD_CANVAS.width, PAD_CANVAS.height, 'map-pads', 0x000000, 0)
    pads.mesh.position.set(0, -view.height / 2 + PAD_STRIP_HEIGHT / 2, OVER_FACE)
    pads.mesh.renderOrder = 1004
    pivot.add(pads.mesh)
  }

  let viewNow: MiniMapView | undefined

  return {
    group,
    get planKey() {
      return planKey
    },
    get padsLit() {
      return padsLit
    },
    update(view, scale) {
      if (view === undefined) {
        if (viewNow !== undefined) dropAll()
        viewNow = undefined
        group.visible = false
        return
      }
      group.visible = true
      lay(view)
      const inverse = scale > 0 ? 1 / scale : 1
      pivot.position.set(view.at[0], view.at[1], MAP_LIFT * inverse)
      pivot.scale.set(inverse, inverse, inverse)
      if (plan !== undefined && view.key !== planKey) {
        planKey = view.key
        paintPlan(plan, view)
      }
      if (pads !== undefined && view.enabled !== padsLit) {
        padsLit = view.enabled
        paintPads(pads, view)
      }
      viewNow = view
    },
    dispose() {
      dropAll()
    },
  }
}
